import { normalizePhone } from "../../../../supabase/functions/_shared/messaging";
import { formatMoney, type MessageRecipient } from "@szerviz/core";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useApp, useFeature } from "../context";
import type { Customer, QuoteChannel, SentMessage } from "../data";
import { ErrorText } from "./ErrorText";
import { Help } from "./Help";

/**
 * Messages to the customer go with channel ticks (owner's rule, 2026-11-04): SMS and e-mail, both ticked by
 * default; SMS only when the "sms" option is on and the customer has a mobile number, e-mail only with an
 * address (otherwise the tick is grey and says why); the program remembers each user's last choice per message
 * type (in this browser); one button sends to every ticked channel.
 */
export type MessageType = "quote" | "ready" | "intake";
export const CHANNELS: readonly QuoteChannel[] = ["email", "sms"];

type Choice = Record<QuoteChannel, boolean>;
const DEFAULT_CHOICE: Choice = { email: true, sms: true };
// the customer's choice keeps its old key; the driver's is remembered on its own (feature "vehicleDriver")
const storageKey = (type: MessageType, userId: string, recipient: MessageRecipient = "customer") =>
  `garagevo.channels.${type}${recipient === "driver" ? ".driver" : ""}.${userId}`;

export function readChoice(type: MessageType, userId: string | null, recipient: MessageRecipient = "customer"): Choice {
  if (!userId) return { ...DEFAULT_CHOICE };
  try {
    const v = JSON.parse(globalThis.localStorage?.getItem(storageKey(type, userId, recipient)) ?? "null") as Partial<Choice> | null;
    return { email: v?.email !== false, sms: v?.sms !== false };
  } catch {
    return { ...DEFAULT_CHOICE };
  }
}

function saveChoice(type: MessageType, userId: string | null, c: Choice, recipient: MessageRecipient = "customer") {
  if (!userId) return;
  try {
    globalThis.localStorage?.setItem(storageKey(type, userId, recipient), JSON.stringify(c));
  } catch {
    // not remembered in this browser; the defaults stay
  }
}

/** Where a message can go: the customer's contact fields, or the driver's (the driver's mobile in `mobile`). */
export type MessageContact = Pick<Customer, "email" | "mobile" | "phone">;

/** The customer's number for SMS: the main (mobile) field, or the older second one. */
export const smsNumberOf = (c: MessageContact | null) => {
  const raw = c?.mobile || c?.phone || "";
  return raw ? normalizePhone(raw) : null;
};

/** Why a channel cannot be used now (an i18n key), or null when it can. */
export function useChannelBlocks(customer: Customer | null, channels: readonly QuoteChannel[] = CHANNELS): Record<QuoteChannel, string | null> {
  const smsOn = useFeature("sms");
  return {
    email: !channels.includes("email") || customer?.email?.trim() ? null : "channels.noEmail",
    sms: !channels.includes("sms") ? null : !smsOn ? "channels.smsOff" : smsNumberOf(customer) ? null : "channels.noMobile",
  };
}

export interface ChannelResult {
  channel: QuoteChannel;
  sent?: SentMessage;
  error?: unknown;
}

/**
 * The ticks and the one send button. `prepare` runs once before sending (e.g. a new link); `send` once per ticked
 * channel. A failed channel does not stop the other one; each result is shown next to the button.
 */
export function ChannelSend({ type, customer, channels = CHANNELS, buttonLabel, prepare, send, onDone, extraMissing = [], primary = true }: {
  type: MessageType;
  customer: Customer | null;
  /** The channels this message knows (the intake link is e-mail only without SMS). */
  channels?: readonly QuoteChannel[];
  buttonLabel: string;
  prepare?: () => Promise<unknown>;
  send: (channel: QuoteChannel, prepared: unknown) => Promise<SentMessage>;
  onDone?: (results: ChannelResult[]) => void | Promise<void>;
  /** Other missing data (shown in the same "still missing" line). */
  extraMissing?: string[];
  primary?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { store, workshop } = useApp();
  const [userId, setUserId] = useState<string | null>(null);
  const [wanted, setWanted] = useState<Choice>(DEFAULT_CHOICE);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<ChannelResult[]>([]);
  const blocks = useChannelBlocks(customer, channels);
  useEffect(() => {
    let live = true;
    void store.currentUser().then((u) => {
      if (!live) return;
      setUserId(u?.id ?? null);
      setWanted(readChoice(type, u?.id ?? null));
    });
    return () => {
      live = false;
    };
  }, [store, type]);

  const shown = CHANNELS.filter((c) => channels.includes(c));
  const ticked = shown.filter((c) => wanted[c] && !blocks[c]);
  const noneAvailable = shown.every((c) => blocks[c]);
  const missing = [...(ticked.length === 0 && !noneAvailable ? [t("channels.missing")] : []), ...extraMissing];
  const money = (minor: number) => formatMoney(minor, workshop.currency, i18n.language);

  const go = async () => {
    setBusy(true);
    setResults([]);
    saveChoice(type, userId, wanted);
    const out: ChannelResult[] = [];
    try {
      const prepared = prepare ? await prepare() : undefined;
      for (const c of ticked) {
        try {
          out.push({ channel: c, sent: await send(c, prepared) });
        } catch (error) {
          out.push({ channel: c, error });
        }
      }
    } catch (error) {
      out.push({ channel: ticked[0] ?? "email", error });
    } finally {
      setResults(out);
      setBusy(false);
    }
    await onDone?.(out);
  };

  return (
    <div className="channel-send">
      <fieldset className="channel-ticks">
        <legend>
          {t("channels.legend")} <Help id="channels" />
        </legend>
        {shown.map((c) => {
          const block = blocks[c];
          return (
            <div key={c} className={`channel-tick${block ? " blocked" : ""}`}>
              <label className="consent-channel">
                <input
                  type="checkbox"
                  checked={!block && wanted[c]}
                  disabled={!!block || busy}
                  onChange={(e) => setWanted((w) => ({ ...w, [c]: e.target.checked }))}
                  aria-label={t(`channels.${c}`)}
                />
                <span>{t(`channels.${c}`)}</span>
              </label>
              {block && <small className="muted">{t(block)}</small>}
            </div>
          );
        })}
      </fieldset>
      {noneAvailable && <p className="warn-text">{t("channels.noneAvailable")}</p>}
      <div className="actions">
        <button type="button" className={primary ? "primary" : undefined} disabled={busy || ticked.length === 0 || extraMissing.length > 0} onClick={() => void go()}>
          {busy ? t("quote.sending") : buttonLabel}
        </button>
        {missing.length > 0 && <span className="missing">{t("required.stillMissing", { fields: missing.join(", ") })}</span>}
      </div>
      {results.length > 0 && (
        <ul className="channel-results" role="status">
          {results.map((r, n) =>
            r.sent ? (
              <li key={n} className="ok">
                {t(r.channel === "email" ? "quote.emailSent" : "quote.smsSent", { to: r.sent.to })}
                {r.sent.price > 0 && ` ${t("sms.priceNote", { price: money(r.sent.price) })}`}
              </li>
            ) : (
              <li key={n}>
                <strong>{t("channels.failedOn", { channel: t(`channels.${r.channel}`) })}</strong>
                <ErrorText error={r.error} />
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  );
}

/** One recipient of a message: the customer or the vehicle's driver (feature "vehicleDriver"). */
export interface MessageTarget {
  key: MessageRecipient;
  /** The person's name on the card. */
  name: string | null;
  contact: MessageContact | null;
  /** Ticked when the card appears (the "car ready" notice: the driver yes, the customer no). */
  ticked: boolean;
}

export interface TargetResult extends ChannelResult {
  recipient: MessageRecipient;
}

/**
 * The same ticks for more than one recipient (owner, 2026-11-05): a card for each (the driver first, then the
 * customer), each with its own tick and its own SMS and e-mail ticks; the channels are remembered per recipient. One
 * button sends to every ticked channel of every ticked recipient. `prepare` runs once per ticked recipient (e.g. a new
 * notice of its own), `send` once per ticked channel; a failure does not stop the others.
 */
export function RecipientsSend({ type, targets, channels = CHANNELS, buttonLabel, prepare, shared = false, send, onDone, note, primary = true }: {
  type: MessageType;
  targets: MessageTarget[];
  channels?: readonly QuoteChannel[];
  buttonLabel: string;
  prepare?: (recipient: MessageRecipient) => Promise<unknown>;
  /** `prepare` once for every recipient (the intake link: one link, sent to both). */
  shared?: boolean;
  send: (recipient: MessageRecipient, channel: QuoteChannel, prepared: unknown) => Promise<SentMessage>;
  onDone?: (results: TargetResult[]) => void | Promise<void>;
  /** Shown above the button (the orange "the driver does not see the amount" warning). */
  note?: ReactNode;
  primary?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { store, workshop } = useApp();
  const [userId, setUserId] = useState<string | null>(null);
  const [on, setOn] = useState<Record<string, boolean>>(() => Object.fromEntries(targets.map((x) => [x.key, x.ticked])));
  const [wanted, setWanted] = useState<Record<string, Choice>>(() => Object.fromEntries(targets.map((x) => [x.key, { ...DEFAULT_CHOICE }])));
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<TargetResult[]>([]);
  const smsOn = useFeature("sms");
  useEffect(() => {
    let live = true;
    void store.currentUser().then((u) => {
      if (!live) return;
      setUserId(u?.id ?? null);
      setWanted(Object.fromEntries(targets.map((x) => [x.key, readChoice(type, u?.id ?? null, x.key)])));
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, type]);

  const shown = CHANNELS.filter((c) => channels.includes(c));
  const blocksOf = (x: MessageTarget): Record<QuoteChannel, string | null> => {
    const driver = x.key === "driver";
    return {
      email: !channels.includes("email") || x.contact?.email?.trim() ? null : driver ? "channels.driverNoEmail" : "channels.noEmail",
      sms: !channels.includes("sms") ? null : !smsOn ? "channels.smsOff" : smsNumberOf(x.contact) ? null : driver ? "channels.driverNoMobile" : "channels.noMobile",
    };
  };
  const plan = targets.map((x) => {
    const blocks = blocksOf(x);
    const ticked = on[x.key] ? shown.filter((c) => wanted[x.key]?.[c] && !blocks[c]) : [];
    return { x, blocks, ticked };
  });
  const total = plan.reduce((a, p) => a + p.ticked.length, 0);
  const money = (minor: number) => formatMoney(minor, workshop.currency, i18n.language);
  const label = (r: MessageRecipient) => t(r === "driver" ? "driver.recipient" : "driver.recipientCustomer");

  const go = async () => {
    setBusy(true);
    setResults([]);
    for (const p of plan) saveChoice(type, userId, wanted[p.x.key] ?? DEFAULT_CHOICE, p.x.key);
    const out: TargetResult[] = [];
    let once: Promise<unknown> | null = null;
    for (const p of plan) {
      if (p.ticked.length === 0) continue;
      try {
        if (prepare && (!shared || !once)) once = prepare(p.x.key);
        const prepared = prepare ? await once : undefined;
        for (const c of p.ticked) {
          try {
            out.push({ recipient: p.x.key, channel: c, sent: await send(p.x.key, c, prepared) });
          } catch (error) {
            out.push({ recipient: p.x.key, channel: c, error });
          }
        }
      } catch (error) {
        out.push({ recipient: p.x.key, channel: p.ticked[0]!, error });
      }
    }
    setResults(out);
    setBusy(false);
    await onDone?.(out);
  };

  return (
    <div className="channel-send">
      <div className="recipient-cards">
        {plan.map(({ x, blocks }) => (
          <fieldset key={x.key} className={`recipient-card${on[x.key] ? " selected" : ""}`}>
            <legend>
              <label className="consent-main">
                <input type="checkbox" checked={!!on[x.key]} disabled={busy} aria-label={label(x.key)} onChange={(e) => setOn((o) => ({ ...o, [x.key]: e.target.checked }))} />
                <span>{label(x.key)}</span>
              </label>
              {x.key === "driver" && <Help id="driver.recipient" />}
            </legend>
            {x.name && <strong className="recipient-name">{x.name}</strong>}
            <div className="channel-ticks" role="group" aria-label={`${label(x.key)}: ${t("channels.legend")}`}>
              {shown.map((c) => {
                const block = blocks[c];
                return (
                  <div key={c} className={`channel-tick${block ? " blocked" : ""}`}>
                    <label className="consent-channel">
                      <input
                        type="checkbox"
                        checked={!block && !!wanted[x.key]?.[c]}
                        disabled={!!block || busy || !on[x.key]}
                        onChange={(e) => setWanted((w) => ({ ...w, [x.key]: { ...(w[x.key] ?? DEFAULT_CHOICE), [c]: e.target.checked } }))}
                        aria-label={`${label(x.key)}: ${t(`channels.${c}`)}`}
                      />
                      <span>{t(`channels.${c}`)}</span>
                    </label>
                    {block && <small className="muted">{t(block)}</small>}
                  </div>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>
      {note}
      <div className="actions">
        <button type="button" className={primary ? "primary" : undefined} disabled={busy || total === 0} onClick={() => void go()}>
          {busy ? t("quote.sending") : buttonLabel}
        </button>
        {total === 0 && <span className="missing">{t("required.stillMissing", { fields: t("driver.missingRecipient") })}</span>}
      </div>
      {results.length > 0 && (
        <ul className="channel-results" role="status">
          {results.map((r, n) =>
            r.sent ? (
              <li key={n} className="ok">
                {label(r.recipient)}: {t(r.channel === "email" ? "quote.emailSent" : "quote.smsSent", { to: r.sent.to })}
                {r.sent.price > 0 && ` ${t("sms.priceNote", { price: money(r.sent.price) })}`}
              </li>
            ) : (
              <li key={n}>
                <strong>{label(r.recipient)}: {t("channels.failedOn", { channel: t(`channels.${r.channel}`) })}</strong>
                <ErrorText error={r.error} />
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  );
}
